<template>
  <div class="simulation-results-page" v-if="!isLoading && simulation">
    <!-- Header -->
    <SimulationHeader 
      :simulation="simulation" 
      :is-refreshing="isRefreshing"
      @refresh="refreshResults" 
    />

    <!-- Simulation Info -->
    <SimulationInfo :simulation="simulation" />

    <!-- Results Content -->
    <div v-if="simulation.status === 'Completed' && simulationResult" class="results-content">
      <!-- Performance Overview -->
      <PerformanceOverview :performance-metrics="simulationResult.performance_metrics" />

      <!-- Asset Performance -->
      <AssetPerformance 
        :asset-list="assetList"
        :transaction-ids="simulationResult.transaction_ids"
        :holdings-on-completion="simulationResult.holdings_on_completion"
        :simulation-parameters="simulation.parameters"
      />

      <!-- Holdings Table -->
      <HoldingsTable :holdings-on-completion="simulationResult.holdings_on_completion" />

      <!-- Save Settings Button -->
      <div class="save-settings-section">
        <button @click="handleSaveSettingsFromResults" :disabled="isSavingSettings" class="btn btn-primary">
          {{ isSavingSettings ? t('simulation.results.saveButton.saving') : t('simulation.results.saveButton.default') }}
        </button>
      </div>
    </div>

    <!-- Status Messages -->
    <StatusMessage 
      v-else-if="['Running', 'Queued', 'Failed'].includes(simulation.status)"
      :status="simulation.status"
      :error-message="simulationResult?.error_message"
    />
  </div>
  
  <!-- Loading State -->
  <div v-else-if="isLoading" class="loading-container">
    <div class="loading-spinner"></div>
    <p>{{ t('simulation.results.loading') }}</p>
  </div>
  
  <!-- Error State -->
  <div v-else-if="error" class="error-container">
    <div class="error-icon">⚠️</div>
    <h3>{{ t('simulation.results.error.title') }}</h3>
    <p>{{ error }}</p>
    <button @click="fetchSimulationData" class="btn btn-primary">{{ t('common.retry') }}</button>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import {
    getSimulationDetails as apiGetSimulationDetails,
    saveSimulationConfig as apiSaveSimulationConfig
} from '@/services/api.js';

// Import components
import SimulationHeader from '@/components/simulation/SimulationHeader.vue';
import SimulationInfo from '@/components/simulation/SimulationInfo.vue';
import PerformanceOverview from '@/components/simulation/PerformanceOverview.vue';
import AssetPerformance from '@/components/simulation/AssetPerformance.vue';
import HoldingsTable from '@/components/simulation/HoldingsTable.vue';
import StatusMessage from '@/components/simulation/StatusMessage.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// Reactive data
const simulation = ref(null);
const simulationResult = ref(null);
const isLoading = ref(false);
const isRefreshing = ref(false);
const isSavingSettings = ref(false);
const error = ref(null);

// Computed properties
const simulationId = computed(() => route.params.id);

const assetList = computed(() => {
  if (!simulationResult.value?.transaction_ids) return [];
  
  const assetsMap = new Map();
  simulationResult.value.transaction_ids.forEach(tx => {
    if (tx.asset_id && (tx.code || tx.ticker || tx.asset_code)) {
      const code = tx.code || tx.ticker || tx.asset_code;
      const name = tx.asset_name || tx.name;
      if (!assetsMap.has(tx.asset_id)) {
        assetsMap.set(tx.asset_id, { 
          id: tx.asset_id, 
          code: code,
          name: name
        });
      }
    }
  });
  
  return Array.from(assetsMap.values());
});

// Methods
const fetchSimulationData = async () => {
  if (!simulationId.value) {
    error.value = t('simulation.results.errors.missingId');
    return;
  }
  
  isLoading.value = true;
  error.value = null;
  
  try {
    const response = await apiGetSimulationDetails(simulationId.value);
    simulation.value = response;
    simulationResult.value = response;
  } catch (err) {
    console.error('Error fetching simulation details:', err);
    error.value = err.message || t('simulation.results.errors.fetchFailed');
  } finally {
    isLoading.value = false;
  }
};

const refreshResults = async () => {
  isRefreshing.value = true;
  await fetchSimulationData();
  isRefreshing.value = false;
};

const handleSaveSettingsFromResults = async () => {
  if (!simulation.value || !simulation.value.parameters) {
    console.error("Simulation data or parameters are missing, cannot save settings.");
    error.value = t('simulation.results.savePrompt.incomplete');
    return;
  }

  const defaultName = t('simulation.results.savePrompt.defaultName', { name: simulation.value.name });
  const settingsName = window.prompt(
    t('simulation.results.savePrompt.message'),
    defaultName
  );

  if (settingsName === null) return;
  if (!settingsName || settingsName.trim() === '') {
    window.alert(t('simulation.results.savePrompt.empty'));
    return;
  }

  isSavingSettings.value = true;
  error.value = null;

  const configData = {
    name: settingsName.trim(),
    parameters: {
      tradingCost: simulation.value.parameters.tradingCost,
      startDate: simulation.value.parameters.startDate,
      endDate: simulation.value.parameters.endDate,
      stopLoss: simulation.value.parameters.stopLoss,
      direction: simulation.value.parameters.direction,
    },
    collectionId: simulation.value.collectionId,
    strategyId: simulation.value.strategyId,
    collectionName: simulation.value.collectionName,
    assetCount: simulation.value.assetCount,
    strategyName: simulation.value.strategyName,
    status: 'Draft'
  };

  try {
    const response = await apiSaveSimulationConfig(configData);
    if (response.success && response.simulation) {
      window.alert(t('simulation.results.savePrompt.success', { name: response.simulation.name }));
      router.push('/simulation/history');
    } else {
      throw new Error(response.message || t('simulation.results.savePrompt.failureFallback'));
    }
  } catch (err) {
    console.error('Failed to save settings from results:', err);
    const reason = err?.message || t('common.unknownError');
    error.value = t('simulation.results.savePrompt.failure', { reason });
  } finally {
    isSavingSettings.value = false;
  }
};

onMounted(() => {
  fetchSimulationData();
});
</script>

<style scoped>
.simulation-results-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.results-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.save-settings-section {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}

.btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  text-decoration: none;
  display: inline-block;
}

.btn-primary {
  background: #1890ff;
  border-color: #1890ff;
  color: white;
}

.btn-primary:hover {
  background: #40a9ff;
  border-color: #40a9ff;
}

/* Loading and Error States */
.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #1890ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.loading-container p,
.error-container p {
  color: #8c8c8c;
  margin-bottom: 16px;
}

.error-container h3 {
  color: #ff4d4f;
  margin-bottom: 8px;
}
</style>
